import { useMemo } from 'react';
import { TimeField } from '@verlo/shared/components/ui/time-field';

/**
 * When something is on (hours plan, 30 Sep 2026) — a lunch menu, a weekend brunch, a
 * Friday-night special.
 *
 * A lunch menu is not a second menu. It is the same menu with some of it on and some of it
 * off depending on the time, so hours are a property of an item or a section:
 *
 *   [{days: [0,1,2,3,4], time_start: '12:00', time_end: '16:00'}, …]
 *
 * A **list**, because "weekday lunch and weekend brunch" is two windows and one row cannot
 * hold it. **Empty means always on**, which is what makes this safe to put on a live menu:
 * a venue that never opens this editor is unaffected.
 *
 * An end before its start is a window that crosses midnight — 22:00–02:00 is a real thing a
 * late bar sells, so it is allowed and labelled rather than refused.
 */

const DAYS = [['Mon', 0], ['Tue', 1], ['Wed', 2], ['Thu', 3], ['Fri', 4], ['Sat', 5], ['Sun', 6]];
const FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAYS = [0, 1, 2, 3, 4];
const WEEKEND = [5, 6];

const sameSet = (a = [], b = []) => a.length === b.length && b.every((d) => a.includes(d));

// One window, in words: "weekdays 12:00–15:00".
const windowWords = (w) => {
  const days = (w.days || []).slice().sort((a, b) => a - b);
  let dayWords = '';
  if (days.length && days.length < 7) {
    if (sameSet(days, WEEKDAYS)) dayWords = 'weekdays';
    else if (sameSet(days, WEEKEND)) dayWords = 'weekends';
    else if (days.length === 1) dayWords = `${FULL[days[0]]}s`;
    else dayWords = days.map((d) => DAYS[d][0]).join('/');
  }
  const t = w.time_start && w.time_end ? `${w.time_start}–${w.time_end}`
    : w.time_start ? `from ${w.time_start}` : w.time_end ? `until ${w.time_end}` : '';
  return [dayWords, t].filter(Boolean).join(' ');
};

// The editor (Back office sweep, Oct 2026: D8, mock "Hours"). Each window is its days, as
// round chips, and a from–to time in the shared time field; "+ Add a time" adds another, so
// weekday lunch and dinner is two windows with the gap between them off.
// `words` replaces the line at the top: { always, set }, for something that isn't a dish.
const HoursEditor = ({ value = [], onChange, what = 'this', words }) => {
  const windows = value || [];
  const set = (i, patch) => onChange(windows.map((w, j) => (j === i ? { ...w, ...patch } : w)));
  const add = () => {
    const last = windows[windows.length - 1];
    // A second time after a lunch is usually the evening on the same days: 18:00–22:00. After
    // that there's no good guess, so the new one starts blank (a blank one isn't saved): a
    // made-up 22:00–22:00 would be on all day.
    const evening = !last?.time_end || last.time_end < '18:00';
    const next = !last ? { days: [], time_start: '12:00', time_end: '15:00' }
      : evening ? { days: last.days || [], time_start: '18:00', time_end: '22:00' }
        : { days: [], time_start: '', time_end: '' };
    onChange([...windows, next]);
  };
  const remove = (i) => onChange(windows.filter((_, j) => j !== i));
  const toggleDay = (i, day) => {
    const days = windows[i].days || [];
    set(i, { days: days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort() });
  };
  const alwaysOn = windows.length === 0;
  const summary = useMemo(() => windows.map(windowWords).filter(Boolean).join(' · '), [windows]);
  const crossesMidnight = (w) => w.time_start && w.time_end && w.time_end <= w.time_start;

  return (
    <div data-testid="hours-editor">
      <p className="text-[13px] text-ink-soft">
        {alwaysOn
          ? (words?.always || `On whenever you are. Add a time to make ${what} a lunch dish, a weekend brunch or a Friday-night special.`)
          : (words?.set || `On the till at these times; outside them it's greyed and can't be added to an order. Bills already open are untouched.`)}
      </p>

      {windows.map((w, i) => (
        <div key={i} className="flex flex-wrap items-center gap-x-3.5 gap-y-2 border-t border-hairline py-3.5 first-of-type:mt-3" data-testid={`hours-window-${i}`}>
          <div className="flex gap-1" role="group" aria-label="Days">
            {DAYS.map(([label, day]) => {
              const on = (w.days || []).includes(day);
              return (
                <button key={day} type="button" onClick={() => toggleDay(i, day)} aria-pressed={on} aria-label={label} title={label}
                  className={`grid h-[34px] w-[34px] place-items-center rounded-full border text-[12.5px] font-semibold ${on ? 'border-verlo bg-verlo text-white' : 'border-hairline-strong bg-white text-ink-soft hover:bg-hover-tint'}`}
                  data-testid={`hours-${i}-day-${day}`}>
                  {label[0]}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 text-[13px] text-ink-soft">
            <TimeField value={w.time_start || ''} onChange={(t) => set(i, { time_start: t })} aria-label="From" testId={`hours-${i}-from`} />
            <span>to</span>
            <TimeField value={w.time_end || ''} onChange={(t) => set(i, { time_end: t })} aria-label="To" testId={`hours-${i}-to`} />
          </div>
          <button type="button" onClick={() => remove(i)} className="ml-auto text-[13px] font-semibold text-signal-stop hover:underline" aria-label="Remove this time" data-testid={`hours-${i}-remove`}>Remove</button>
          {((w.days || []).length === 0 || crossesMidnight(w)) && (
            <div className="basis-full text-[12px] text-ink-faint">
              {(w.days || []).length === 0 && 'No days picked: every day. '}
              {crossesMidnight(w) && <span className="text-signal-warn">Runs past midnight.</span>}
            </div>
          )}
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-3 border-t border-hairline pt-3.5">
        <button type="button" onClick={add} className="text-[13.5px] font-semibold text-verlo hover:underline" data-testid="hours-add">+ Add a time</button>
        {!alwaysOn && <button type="button" onClick={() => onChange([])} className="text-[13px] text-ink-faint hover:underline" data-testid="hours-clear">Always on</button>}
        {summary && <span className="ml-auto text-[12.5px] text-ink-soft" data-testid="hours-summary">{summary[0].toUpperCase() + summary.slice(1)}</span>}
      </div>
    </div>
  );
};

/** Hours in words, for a line that isn't the editor: "Sundays 12:00–17:00". Blank if always on. */
export const hoursWords = (windows) => {
  const s = (windows || []).map(windowWords).filter(Boolean).join(' · ');
  return s ? s[0].toUpperCase() + s.slice(1) : '';
};

/** Blanks gone, days sorted — what the API stores. A window with nothing set at all is
 *  dropped rather than saved, since the server refuses one and "empty" already means
 *  always on. */
export const normaliseHours = (windows) => (windows || [])
  .map((w) => {
    const out = {};
    const days = (w.days || []).map(Number).filter((d) => d >= 0 && d <= 6);
    if (days.length && days.length < 7) out.days = [...new Set(days)].sort((a, b) => a - b);
    if (w.time_start) out.time_start = String(w.time_start).slice(0, 5);
    if (w.time_end) out.time_end = String(w.time_end).slice(0, 5);
    if (w.start) out.start = String(w.start).slice(0, 10);
    if (w.end) out.end = String(w.end).slice(0, 10);
    return out;
  })
  .filter((w) => Object.keys(w).length > 0);

export default HoursEditor;
